import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ANDROID_STORE_URL, IOS_STORE_URL } from '@components/common/AppStoreButtons';
import useAppStore from '@store/useAppStore';
import { getAllToken } from '@util/token';
import styled, { keyframes } from 'styled-components';

const POPUP_DISMISSED_KEY = 'letmeup_mobile_app_popup_dismissed_until';
const POPUP_SEEN_SESSION_KEY = 'letmeup_mobile_app_popup_seen_session';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

type MobileOS = 'ios' | 'android' | 'other';

interface MobileAppInstallPopupProps {
  forceShow?: boolean;
}

const detectMobileOS = (): { isMobile: boolean; os: MobileOS } => {
  const ua = navigator.userAgent || navigator.vendor || (window as any)?.opera || '';
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any)?.MSStream) return { isMobile: true, os: 'ios' };
  if (/android/i.test(ua)) return { isMobile: true, os: 'android' };
  return { isMobile: false, os: 'other' };
};

const isWebViewOrStandalone = (): boolean => {
  const ua = navigator.userAgent || '';
  if (/LetMeUp|letmeup/i.test(ua)) return true;
  if (/\bwv\b/i.test(ua)) return true;
  if (/(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua)) {
    return true;
  }
  if ((window.navigator as any).standalone === true) return true;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  return false;
};

const isStaffPath = (pathname: string): boolean =>
  [
    '/backoffice',
    '/admin',
    '/setting',
    '/dashboard',
    '/product',
    '/sales',
    '/message',
    '/seat',
    '/kiosk',
    '/merchant',
  ].some((prefix) => pathname.startsWith(prefix));

const isDismissed = (): boolean => {
  const dismissedUntil = Number(localStorage.getItem(POPUP_DISMISSED_KEY) || 0);
  if (!dismissedUntil) return false;
  if (Date.now() < dismissedUntil) return true;
  localStorage.removeItem(POPUP_DISMISSED_KEY);
  return false;
};

const MobileAppInstallPopup = ({ forceShow = false }: MobileAppInstallPopupProps): React.ReactElement | null => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [dismissForWeek, setDismissForWeek] = useState(false);
  const userPhoneNumber = useAppStore((state) => state.userPhoneNumber);

  const targetOS = useMemo(() => detectMobileOS(), []);

  useEffect(() => {
    if (forceShow) {
      setIsVisible(true);
      return;
    }

    const { userToken, merchantToken, adminToken } = getAllToken();
    const isAdmin = !!adminToken;
    const isMerchantOnly = !!merchantToken && !userToken && !userPhoneNumber;
    const pathBeforeLogin = localStorage.getItem('locationBeforeRedirection') || '';

    if (!targetOS.isMobile) return;
    if (isWebViewOrStandalone()) return;
    if (isStaffPath(location.pathname) || isStaffPath(pathBeforeLogin)) return;
    if (isAdmin || isMerchantOnly) return;
    if (sessionStorage.getItem(POPUP_SEEN_SESSION_KEY) === 'true') return;
    if (isDismissed()) return;

    const timer = window.setTimeout(() => {
      sessionStorage.setItem(POPUP_SEEN_SESSION_KEY, 'true');
      setIsVisible(true);
    }, 800);

    return () => window.clearTimeout(timer);
  }, [forceShow, location.pathname, targetOS.isMobile, userPhoneNumber]);

  const close = () => {
    if (dismissForWeek) {
      localStorage.setItem(POPUP_DISMISSED_KEY, String(Date.now() + DISMISS_DURATION_MS));
    }
    setIsVisible(false);
  };

  const install = () => {
    const url = targetOS.os === 'ios' ? IOS_STORE_URL : ANDROID_STORE_URL;
    window.open(url, '_blank', 'noopener,noreferrer');
    close();
  };

  if (!isVisible) return null;

  return (
    <Overlay role="presentation" onClick={close} data-testid="mobile-app-install-popup">
      <PopupContainer role="dialog" aria-modal="true" aria-labelledby="mobile-app-install-title" onClick={(e) => e.stopPropagation()}>
        <CloseButton type="button" onClick={close} aria-label="닫기">
          x
        </CloseButton>

        <AppLogo src="/logo192.png" alt="" />
        <Title id="mobile-app-install-title">렛미업 앱으로 더 편하게 이용하세요</Title>
        <Description>이용권 구매, 좌석 확인, 잔여시간 확인을 앱에서 바로 처리할 수 있습니다.</Description>

        <FeatureList>
          <FeatureItem>이용권 구매 및 연장</FeatureItem>
          <FeatureItem>실시간 좌석과 잔여시간 확인</FeatureItem>
          <FeatureItem>결제와 출입 알림 확인</FeatureItem>
        </FeatureList>

        <InstallButton type="button" onClick={install}>
          앱 설치하기
        </InstallButton>

        <CheckboxLabel>
          <Checkbox
            type="checkbox"
            checked={dismissForWeek}
            onChange={(e) => setDismissForWeek(e.target.checked)}
          />
          7일 동안 다시 보지 않기
        </CheckboxLabel>
      </PopupContainer>
    </Overlay>
  );
};

export default MobileAppInstallPopup;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    transform: translateY(1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.48);
  animation: ${fadeIn} 0.2s ease-out;
`;

const PopupContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 25rem;
  padding: 1.75rem 1.25rem 1.25rem;
  border-radius: 1rem;
  background: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.neutralGray[600]};
  text-align: center;
  box-shadow: 0 1rem 2.5rem rgba(15, 23, 42, 0.2);
  animation: ${slideUp} 0.22s ease-out;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 50%;
  background: ${({ theme }) => theme.neutralGray[100]};
  color: ${({ theme }) => theme.neutralGray[500]};
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
`;

const AppLogo = styled.img`
  width: 4rem;
  height: 4rem;
  margin-bottom: 1rem;
  border-radius: 0.875rem;
  box-shadow: 0 0.375rem 1rem rgba(0, 190, 193, 0.2);
`;

const Title = styled.h2`
  margin: 0 1.25rem 0.625rem;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.35;
`;

const Description = styled.p`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.neutralGray[500]};
  font-size: 0.875rem;
  line-height: 1.5;
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0 0 1.25rem;
  padding: 0;
  list-style: none;
`;

const FeatureItem = styled.li`
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.main[100]};
  color: ${({ theme }) => theme.neutralGray[500]};
  font-size: 0.85rem;
`;

const InstallButton = styled.button`
  width: 100%;
  min-height: 3rem;
  margin-bottom: 0.875rem;
  border: 0;
  border-radius: 0.75rem;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.neutralGray[400]};
  font-size: 0.8rem;
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
  accent-color: ${({ theme }) => theme.primary};
`;
